import { ActivitySpinnerSize, ActivitySpinnerWidget } from '@tableau/widgets';
import React from 'react';
import { connect } from 'react-redux';
import { Redirect, Route, RouteComponentProps, Switch } from 'react-router-dom';
import { bindActionCreators } from 'redux';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { MapDispatchToProps, MapStateToProps } from '~commonInfra/utils/ConnectUtils';
import { AllSiteUsersAndIdentitiesForSingleSiteSpaceRouter } from '~src/authentication/components/userIdentitiesSpace/SiteUsers/AllSiteUsersAndIdentitiesForSingleSiteSpaceRouter';
import { BackgroundJobsSpaceProvider } from '~src/backgroundJob/components/BackgroundJobsSpaceProvider';
import { parseJobIdQueryParam } from '~src/backgroundJob/utils/BackgroundJobUrls';
import { siteUrlsSelector } from '~src/bootstrap/redux/selectors/SiteUrlsSelectors';
import { AppState } from '~src/bootstrap/redux/state/AppState';
import { ConnectedReduxRouterErrorWrapper } from '~src/bootstrap/routerHelpers/ReduxRouterErrorWrapper';
import { RouterError } from '~src/bootstrap/routerHelpers/RouterError';
import { SiteBroadcastsSpaceProvider } from '~src/broadcasts/space/SiteBroadcastsSpaceProvider';
import { CatalogProviders } from '~src/catalog/CatalogProviders';
import { AllLabelsSpaceProvider } from '~src/catalog/components/labelValues/AllLabelsSpaceProvider';
import { ConnectedCatalogBrowserHost } from '~src/catalog/ConnectedCatalogBrowserHost';
import { CollectionsSpaceRouter } from '~src/Collections/space/CollectionsSpaceRouter';
import { CollectionUtils } from '~src/Collections/utils/CollectionUtils';
import { ContentRelayUtils } from '~src/contentRelay/utils/ContentRelayUtils';
import { DatasourceSpaceRouter } from '~src/datasource/components/DatasourceSpaceRouter';
import { ExploreDatasourceProvider } from '~src/datasource/components/ExploreDatasourceProvider';
import { RedirectToFavoritedDatasourceProvider } from '~src/datasource/components/RedirectToFavoritedDatasourceProvider';
import { AddUserToDataAlertProvider, RemoveUserFromDataAlertProvider } from '~src/dda/components/AddOrRemoveFromDataAlertProvider';
import { ConnectedFavoritesSpace } from '~src/favorites/space/ConnectedFavoritesSpace';
import { FlowSpaceFavoriteRedirect } from '~src/flow/components/FlowSpaceFavoriteRedirect';
import { FlowSpaceRouter } from '~src/flow/components/FlowSpaceRouter';
import { HomeSpaceRouter } from '~src/home/components/HomeSpaceRouter';
import { PersonalSpaceProvider } from '~src/personalSpace/components/spaceProviders/PersonalSpaceProvider';
import { ProjectSpaceRouter } from '~src/projects/components/ProjectSpaceRouter';
import { RedirectToFavoritedProjectProvider } from '~src/projects/components/RedirectToFavoritedProjectProvider';
import { PublishedConnectionSpaceFavoriteRedirect } from '~src/publishedConnection/components/PublishedConnectionSpaceFavoriteRedirect';
import { PublishedConnectionSpaceRouter } from '~src/publishedConnection/components/PublishedConnectionSpaceRouter';
import { PublishedConnectionTableSpaceFavoriteRedirect } from '~src/publishedConnectionTable/components/PublishedConnectionTableSpaceFavoriteRedirect';
import { PublishedConnectionTableSpaceRouter } from '~src/publishedConnectionTable/components/PublishedConnectionTableSpaceRouter';
import { RecentsSpaceProvider } from '~src/recents/space/RecentsSpaceProvider';
import { RecommendationsSpaceProvider } from '~src/recommendations/components/spaces/RecommendationsSpaceProvider';
import { ConnectedRecycleBinSpace } from '~src/recycleBin/space/ConnectedRecycleBinSpace';
import { RecycleBinUtils } from '~src/recycleBin/utils/RecycleBinUtils';
import { AllSchedulesSpaceProvider } from '~src/schedule/components/AllSchedulesSpaceProvider';
import { ScheduleSpaceRouter } from '~src/schedule/components/ScheduleRouter';
import { isSelfServiceSchedulingEnabledForAllContentTypes } from '~src/schedule/utils/ScheduleUtils';
import { AISearchHost } from '~src/search/components/AISearchHost';
import { ConnectedSearchMicroViewHostWithGenericPackage } from '~src/search/components/SearchMicroviewHostV2';
import { SiteStatusSpaceProvider } from '~src/serverStatus/components/siteStatusSpace/SiteStatusSpaceProvider';
import { AuthLevel, getSessionManagementServiceInstance, SessionManagementService } from '~src/sessionManagement/SessionManagementService';
import { IntegrationsSubspaceName } from '~src/settings/IntegrationsSettings/IntegrationsSettingsSubspace';
import { MobileSettingsSubspaceName } from '~src/settings/mobile-settings/MobileSecuritySettingsShared';
import { ServerSettingsUnauthenticated } from '~src/settings/services/ServerSettingsUnauthenticated';
import { SharedWithMeSpaceProvider } from '~src/sharing/components/sharedWithMe/SharedWithMeSpaceProvider';
import { fetchSiteAdminResources } from '~src/siteManagement/actions/SiteAdminResourceActions';
import { AdminAnalysisVizSpaceBreadcrumbsAndTitleUpdater } from '~src/spacesPlatform/components/AnalysisVizSpace/AdminAnalysisVizSpaceBreadcrumbsAndTitleUpdater';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { selectPreviousUrl } from '~src/spacesPlatform/selectors/NavigationStateSelectors';
import { SiteSpaceContentType } from '~src/spacesPlatform/state/SiteSpaceContentTypeState';
import { SiteSettingsSubspaceName } from '~src/spacesPlatform/state/SpaceInfo';
import { StartersSpaceProvider } from '~src/starters/components/StartersSpaceProvider';
import { SiteTasksSpaceRouter } from '~src/tasks/components/siteTasksSpace/SiteTasksSpaceRouter';
import { TaskDetailsSpaceProvider } from '~src/tasks/components/taskDetailsSpace/TaskDetailsSpaceProvider';
import { AllGroupsSpaceProvider } from '~src/userManagement/groups/components/space/AllGroupsSpaceProvider';
import { GroupSpaceRouter } from '~src/userManagement/groups/components/space/GroupSpaceRouter';
import { AllGroupSetsSpaceProvider } from '~src/userManagement/groupsets/components/space/AllGroupSetsSpaceProvider';
import { GroupSetSpaceRouter } from '~src/userManagement/groupsets/components/space/GroupSetSpaceRouter';
import { isGroupSetsEnabledForSite } from '~src/userManagement/groupsets/selectors/GroupSetSessionInfoSelectors';
import { SiteUserSpaceRouter } from '~src/userManagement/users/components/siteUserSpace/SiteUserSpaceRouter';
import { AllSiteUsersSpaceProvider } from '~src/userManagement/users/components/siteUsersSpace/AllSiteUsersSpaceProvider';
import { ConnectedRedirectToViewSpaceProvider, RedirectViewType } from '~src/viz/components/RedirectToViewProvider';
import { VizSpaceUrlUpdater } from '~src/viz/components/VizSpaceUrlUpdater';
import { VizUrl } from '~src/viz/helpers/VizUrlHelpers';
import { RedirectToFavoritedWorkbookProvider } from '~src/workbook/components/RedirectToFavoritedWorkbookProvider';
import { WorkbookSpaceRouter } from '~src/workbook/components/WorkbookSpaceRouter';

import { ConnectedSiteSettingsSpaceProvider as SiteSettingsSpaceProvider } from '../../settings/components/subspaces/SiteSettingsSpace/SiteSettingsSpaceProvider';
import { SiteSpaceProvider, SiteSpaceProviderProps } from '../components/siteSpace/SiteSpaceProvider';
import isRecycleBinEnabled = RecycleBinUtils.isRecycleBinEnabled;

import { SessionSiteSelectors, SessionUserSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';

export interface SiteSpaceRouterOwnProps {
  readonly siteName: string;
  readonly routeLocationPath: string;
  readonly routeLocationSearch: string;
}

export interface SiteSpaceRouterStateProps {
  readonly previousUrl: string;
  readonly urls: SpacesSiteUrls;
  readonly routerReactKey: number;
  readonly canPublish: boolean;
  readonly allowedSiteAdminResources?: Set<string>;
  readonly isFetchingSiteAdminResources: boolean;
  readonly canUseAISearch: boolean;
}

export interface SiteSpaceRouterDispatchProps {
  readonly fetchSiteAdminResources: () => void;
}

export type SiteSpaceRouterComponentProps = SiteSpaceRouterOwnProps & SiteSpaceRouterStateProps & SiteSpaceRouterDispatchProps;

export class SiteSpaceRouterComponent extends React.PureComponent<SiteSpaceRouterComponentProps> {
  private server: SessionManagementService;

  public constructor(props: SiteSpaceRouterComponentProps) {
    super(props);
    this.server = getSessionManagementServiceInstance();
  }

  public componentDidMount() {
    // Populate allowedSiteAdminResources so admin-profile route guards (canAccessAdminRoute)
    // resolve correctly on a cold deep-link / refresh. NavPanel also fetches this, but it may not
    // have completed when a settings sub-route (e.g. /settings/packages) is loaded directly. No-op
    // for admins, who bypass the resource check.
    if (this.server.isAdminProfilesEnabled()) {
      this.props.fetchSiteAdminResources();
    }
  }

  /**
   * Check if the current user can access an admin route for a specific resource type.
   * 
   * @param resourceType - Resource type name (e.g., 'User', 'Group', 'Task', 'Job')
   * @returns true if user can access the route, false otherwise
   * 
   * Logic:
   * 1. ALWAYS allow if user is a site/server admin (both old and new behavior)
   * 2. NEW BEHAVIOR (FF ON): Check if user has specific resource permission from allowedSiteAdminResources
   * 3. OLD BEHAVIOR (FF OFF): If not admin, deny access (maintains original blocking behavior)
   */
  private canAccessAdminRoute(resourceType: string): boolean {
    // ALWAYS allow if user is a site/server admin (both old and new behavior)
    if (this.server.isUserAuthorized(AuthLevel.SiteAdmin)) {
      return true;
    }

    // When admin profiles is enabled, check specific resource permissions
    if (this.server.isAdminProfilesEnabled()) {
      const allowedResources = this.props.allowedSiteAdminResources;
      return allowedResources?.has(resourceType) ?? false;
    }

    // OLD BEHAVIOR: When FF is disabled and user is not admin, deny access
    // This maintains exact original behavior - non-admins are blocked
    return false;
  }

  /** Shown when fetching admin permissions; does not grant or deny access. */
  private adminRouteLoadingContent(): React.ReactNode {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 200 }}>
        <ActivitySpinnerWidget spinnerSize={ActivitySpinnerSize.Large} />
      </div>
    );
  }

  public render() {
    return (
      <Switch>
        <Route path={this.props.urls.searchPath()} render={() => (
          FeatureFlags.ces_AISearchEnabled && this.props.canUseAISearch ?
            <div>
              <AISearchHost />
              <div style={{ position: 'relative' }}>
                <ConnectedSearchMicroViewHostWithGenericPackage />
              </div>
            </div>
            : <ConnectedSearchMicroViewHostWithGenericPackage />
        )} />
        <Route exact path={this.props.urls.projectsPath()} render={this.siteSpaceprovider('project')} />
        <Route exact path={this.props.urls.explorePath()} render={this.siteSpaceprovider('explore')} />
        {
          // access recycle bin only when its enabled and the user can publish
          isRecycleBinEnabled() && this.props.canPublish && this.getRecycleBinRoute()
        }
        {this.server.flowsOnSiteEnabled() &&
          <Route exact path={this.props.urls.flowsPath()} render={this.siteSpaceprovider('flow')} />
        }
        {FeatureFlags.publishedConnectionContentManagementEnabled &&
          <Route exact path={this.props.urls.virtualConnectionsPath()} render={this.siteSpaceprovider('virtualConnection')} />
        }
        <Route exact path={this.props.urls.viewsPath()} render={this.siteSpaceprovider('view')} />


        <Route path={this.props.urls.viewGrantAccessPath(':workbookName', ':viewName')} render={({ match }) =>
            <Redirect to={this.props.urls.viewPath(`${match.params.workbookName}/${match.params.viewName}`)} />
        } />

        {this.getVizRoute()}
        <Route path={this.props.urls.redirectToFavoriteViewPath(':id')} render={({ match }) => this.redirectToViewProvider(match.params.id, 'favorite')} />
        <Route path={this.props.urls.redirectToViewPath(':id')} render={({ match }) => this.redirectToViewProvider(match.params.id, 'regular')} />
        <Route path={this.props.urls.redirectToCustomViewPathWithLuid(':viewId', ':customViewLuid', ':customViewUrlId')} render={({ match }) => this.redirectToViewProvider(match.params.viewId, 'customView', undefined, match.params.customViewUrlId, match.params.customViewLuid)} />

        <Route exact path={this.props.urls.workbooksPath()} render={this.siteSpaceprovider('workbook')} />
        <Route path={this.props.urls.workbookPath(':id')} render={() => <WorkbookSpaceRouter {...this.props} />} />
        <Route exact path={this.props.urls.redirectToWorkbookPath(':id')} render={({ match }) => this.redirectToFavoritedWorkbookProvider(match.params.id)} />

        {this.getAllGroupsRoute(this.server)}

        { isGroupSetsEnabledForSite() && this.getAllGroupSetsRoute(this.server)}
        { isGroupSetsEnabledForSite() && this.getGroupSetRoute(this.server)}

        {CollectionUtils.checkCollectionSiteSetting() && this.getCollectionsRoute()}

        {ContentRelayUtils.isContentRelayEnabled() && this.getContentRelayPlansRoute()}

        {FeatureFlags.broadcastUI && FeatureFlags.broadcastEnabled && this.getBroadcastsRoute()}

        <Route exact path={this.props.urls.datasourcesPath()} render={this.siteSpaceprovider('datasource')} />
        <Route path={this.props.urls.datasourcePath(':id')} render={() => <DatasourceSpaceRouter {...this.props} />} />
        {/* // explore/datasource navigates to the data source by its Published Datasource ID, rather than the Unified Datasource (connection) ID */}
        <Route exact path={this.props.urls.exploreDatasourcePath(':id')} render={({ match }) => this.exploreDatasourceProvider(match.params.id)} />
        <Route exact path={this.props.urls.favoriteDatasourcePath(':id', ':idForFavoriting')} render={({ match }) => this.redirectToFavoritedDatasourceProvider(match.params.idForFavoriting, match.params.id)} />
        <Route exact path={this.props.urls.favoriteDatasourceDoesntExistPath(':idForFavoriting')} render={({ match }) => this.redirectToFavoritedDatasourceProvider(match.params.idForFavoriting)} />

        <Route path={this.props.urls.flowPath(':id')} render={() => <FlowSpaceRouter {...this.props} />} />
        <Route exact path={this.props.urls.redirectToFlowPath(':id')} render={({ match }) => this.redirectToFlowProvider(match.params.id)} />

        {
          FeatureFlags.publishedConnectionContentManagementEnabled &&
          <Route path={this.props.urls.virtualConnectionPath(':id')} render={() => <PublishedConnectionSpaceRouter {...this.props} />} />
        }
        {
          FeatureFlags.publishedConnectionContentManagementEnabled &&
          <Route exact path={this.props.urls.redirectToVirtualConnectionPath(':id')} render={({ match }) => this.redirectToPublishedConnectionProvider(match.params.id)} />
        }

        {
          FeatureFlags.publishedConnectionContentManagementEnabled &&
          <Route path={this.props.urls.virtualConnectionTablePath(':id')} render={() => <PublishedConnectionTableSpaceRouter {...this.props} />} />
        }
        {
          FeatureFlags.publishedConnectionContentManagementEnabled &&
          <Route exact path={this.props.urls.redirectToVirtualConnectionTablePath(':id')} render={({ match }) => this.redirectToPublishedConnectionTableProvider(match.params.id)} />
        }

        {this.getUsersRoute(this.server)}
        <Route path={this.props.urls.baseUserPath()} render={() => <SiteUserSpaceRouter {...this.props} />} />

        <Route path={this.props.urls.projectPath(':id')} render={() => <ProjectSpaceRouter {...this.props} />} />
        <Route exact path={this.props.urls.redirectToProjectPath(':id')} render={({ match }) => this.redirectToFavoritedProjectProvider(match.params.id)} />

        <Route path={this.props.urls.favoritesPath()} render={this.favoritesSpace} />
        <Route path={this.props.urls.recentsPath()} render={this.recentsSpace} />
        <Route path={this.props.urls.homePath()} render={() => <HomeSpaceRouter {...this.props} />} />
        <Route path={this.props.urls.recommendationListPath()} render={() => <RecommendationsSpaceProvider {...this.props} />} />

        {this.getSiteStatusRoute(this.server)}
        {this.getSiteStatusVizRoute(this.server)}

        {this.getSiteTasksRoute(this.server)}
        {FeatureFlags.labelManagementEnabled && this.getLabelsRoute(this.server)}
        {
          FeatureFlags.jobManagementEnabled && FeatureFlags.backgroundJobsContentAndTaskDetailsEnabled &&
          this.getSiteTaskDetailsRoute(this.server)
        }
        {
          FeatureFlags.jobManagementEnabled &&
          this.getSiteBackgroundJobsRoute(this.server)
        }
        {
          this.getDataCatalogRoute(this.server)
        }
        <Route path={this.props.urls.addUserToDataAlertPath(':dataAlertId')} render={({ match }) => <AddUserToDataAlertProvider dataAlertId={match.params.dataAlertId} />} />
        <Route path={this.props.urls.removeUserFromDataAlertPath(':dataAlertId')} render={({ match }) => <RemoveUserFromDataAlertProvider dataAlertId={match.params.dataAlertId} />} />

        {this.getGroupRoute(this.server)}
        {this.getScheduleRoute(this.server)}
        {this.getAllSchedulesRoute(this.server)}
        {this.settingsRoute(this.props.urls.settingsPath(), 'settings')}
        {FeatureFlags.extensionsSettingsTabEnabled && this.settingsRoute(this.props.urls.settingsExtensionsPath(), 'extensions')}
        {FeatureFlags.packagesEnabled && this.settingsRoute(this.props.urls.settingsPackagesPath(), 'packages')}
        {FeatureFlags.packagesEnabled && this.settingsRoute(this.props.urls.packageDetailsPath(':luid'), 'packages')}
        {
          ServerSettingsUnauthenticated.isSiteSamlEnabled() &&
          this.settingsRoute(this.props.urls.settingsAuthenticationPath(), 'authentication')
        }
        {
          this.settingsRoute(this.props.urls.settingsAuthenticationPath() + '/editConfiguration/' + ':id', 'authentication')
        }
        {
          this.settingsRoute(this.props.urls.settingsAuthenticationPath() + '/newConfiguration/', 'authentication')
        }
        {
          FeatureFlags.multipleScimConfigurationsEnabled &&
          this.settingsRoute(this.props.urls.settingsAuthenticationPath() + '/newScimConfiguration/', 'authentication')
        }
        {
          FeatureFlags.multipleScimConfigurationsEnabled &&
          this.settingsRoute(this.props.urls.settingsAuthenticationPath() + '/editScimConfiguration/' + ':id', 'authentication')
        }
        {
          ServerSettingsUnauthenticated.isBridgeEnabled() &&
          this.settingsRoute(this.props.urls.settingsBridgePath(), 'bridge')
        }
        {
          ServerSettingsUnauthenticated.isBridgeEnabled() &&
          FeatureFlags.enableDataConnect &&
          this.settingsRoute(this.props.urls.settingsDataConnectPath(), 'dataconnect')
        }
        {
          FeatureFlags.enablePrivateConnect &&
          this.settingsRoute(this.props.urls.settingsPrivateConnectPath(), 'privateconnect')
        }
        {
          this.settingsRoute(this.props.urls.settingsConnectedApplicationsPath(), 'connectedApplications')
        }
        {
          // Needed since the connected applications settings subspace has an additional nested subspace
          this.settingsRoute(this.props.urls.connectedApplicationDirectTrustPath(':id'), 'connectedApplications')
        }
        {
          this.settingsRoute(this.props.urls.connectedApplicationEasPath(':id'), 'connectedApplications')
        }
        {
          FeatureFlags.securitySettingsEnabled &&
          this.settingsRoute(this.props.urls.settingsSecurityPath(), 'security')
        }
        {this.settingsRoute(this.props.urls.settingsMobilePath(), MobileSettingsSubspaceName as SiteSettingsSubspaceName)}
        {this.getStartersRouteByConnectionType()}
        {this.server.isNamedSharingEnabled() && this.getSharedWithMeRoute()}
        {this.getPersonalSpaceRoute(this.server)}
          {this.settingsRoute(this.props.urls.settingsIntegrationsPath(), IntegrationsSubspaceName as SiteSettingsSubspaceName)}
          <Route render={this.error} />
      </Switch>
    );
  }

  private settingsRoute(path: string, subspaceName: SiteSettingsSubspaceName) {
    // isSiteAdminSettingsEnabled is set on the backend (true for online and false for on-prem),
    // so that site admin can access settings only in online environment.
    const canAccessSettings = this.server.isSessionServerAdmin()
      || (this.server.isSessionSiteAdmin() && this.server.isSiteAdminSettingsEnabled())
      || (FeatureFlags.adminProfilesForSiteSettingsEnabled && this.canAccessAdminRoute('Setting'));

    if (canAccessSettings) {
      return <Route exact path={path} render={this.settingsProvider(subspaceName)} />;
    }

    return this.adminPermissionsLoadingRoute(path, true);
  }

  /**
   * For admin-profile users whose permissions fetch is in flight, render a
   * loading spinner instead of falling through to the router error page.
   * Returns undefined for admins (who took the normal route above) and for
   * users whose fetch is settled (they correctly get the error page).
   */
  private adminPermissionsLoadingRoute(path: string, exact: boolean = false): React.ReactNode {
    if (this.server.isAdminProfilesEnabled()
        && this.props.isFetchingSiteAdminResources
        && !this.server.isUserAuthorized(AuthLevel.SiteAdmin)) {
      return <Route exact={exact} path={path} render={() => this.adminRouteLoadingContent()} />;
    }
  }

  private getRecycleBinRoute(){
    return <Route
        path={this.props.urls.recycleBinPath()}
        render={this.recycleBinSpace}
      />;
  }

  private settingsProvider(subspaceName: SiteSettingsSubspaceName) {
    return () =>
      <SiteSettingsSpaceProvider
        urls={this.props.urls}
        siteName={this.props.siteName}
        subspaceName={subspaceName}
      />;
  }

  private getDataCatalogRoute(server: SessionManagementService) {
    return server.catalogingOnSiteEnabled() &&
      <Route path={this.props.urls.catalogPath()}
        render={() => <ConnectedCatalogBrowserHost />}
      />;
  }

  private getSiteBackgroundJobsRoute(server: SessionManagementService) {
    return server.isUserAuthorized(AuthLevel.SiteAdmin) &&
      <Route path={this.props.urls.jobsPath()}
        render={(routeProps) => <BackgroundJobsSpaceProvider
          siteName={this.props.siteName}
          routeLocationSearch={routeProps.location.search}
          routeLocationPath={routeProps.location.pathname}
        />}
      />;
  }

  private getSiteTaskDetailsRoute(server: SessionManagementService) {
    return server.isUserAuthorized(AuthLevel.SiteAdmin) &&
      <Route
        exact path={`${this.props.urls.jobsPath()}/task/:taskLuid`}
        render={(routeProps) => <TaskDetailsSpaceProvider
          taskLuid={routeProps.match.params.taskLuid}
          jobsUrl={this.props.urls.jobsUrl()}
          jobId={parseJobIdQueryParam(routeProps.location.search)}
        />}
      />;
  }

  private getAllSchedulesRoute(server: SessionManagementService) {
    return !isSelfServiceSchedulingEnabledForAllContentTypes() && server.isUserAuthorized(AuthLevel.SiteAdmin) &&
      <Route exact path={this.props.urls.schedulesPath()}
        render={(routeProps) => <AllSchedulesSpaceProvider
          siteName={this.props.siteName}
          routeLocationSearch={routeProps.location.search}
          routeLocationPath={routeProps.location.pathname}
          urls={this.props.urls}
        />}
      />;
  }

  private getBroadcastsRoute() {
    return <Route
      path={this.props.urls.broadcastsPath()}
      render={(props: RouteComponentProps) =>
        <SiteBroadcastsSpaceProvider
          siteName={this.props.siteName}
          routeLocationPath={props.location.pathname}
          routeLocationSearch={props.location.search} />}
    />;
  }

  private getCollectionsRoute() {
    return <Route
      path={this.props.urls.collectionsPath()}
      render={() =>
        <CollectionsSpaceRouter
          urls={this.props.urls}
          siteName={this.props.siteName}
          previousUrl={this.props.previousUrl}
        />}
    />;
  }

  private getContentRelayPlansRoute() {
    // Content Relay Plans render inline in the site-level Explore space (like views, data sources, etc.)
    // via siteSpaceprovider, so the "All Relay Plans" filter shows the same list-view grid as the other
    // content types instead of navigating to a standalone space.
    return [
      <Route
        key={'allContentRelayPlans'}
        exact
        path={this.props.urls.allContentRelayPlansPath()}
        render={this.siteSpaceprovider('contentRelayPlan')}
      />,
      <Route
        key={'contentRelayPlansRedirect'}
        exact
        path={this.props.urls.contentRelayPlansPath()}
        render={() => <Redirect to={this.props.urls.allContentRelayPlansPath()} />}
      />,
    ];
  }

  private getAllGroupsRoute(server: SessionManagementService) {
    return this.canAccessAdminRoute('Group') &&
      <Route exact path={this.props.urls.groupsPath()}
        render={(routeProps) => <AllGroupsSpaceProvider
          siteName={this.props.siteName}
          routeLocationSearch={routeProps.location.search}
          routeLocationPath={routeProps.location.pathname}
          urls={this.props.urls}
        />}
      />;
  }

  private getAllGroupSetsRoute(server: SessionManagementService) {
    return server.isUserAuthorized(AuthLevel.Authenticated) &&
      <Route exact path={this.props.urls.groupSetsPath()}
        render={(routeProps) => <AllGroupSetsSpaceProvider
          siteName={this.props.siteName}
          routeLocationSearch={routeProps.location.search}
          routeLocationPath={routeProps.location.pathname}
          urls={this.props.urls}
        />}
      />;
  }

  private getGroupSetRoute(server: SessionManagementService) {
    return server.isUserAuthorized(AuthLevel.Authenticated) &&
      <Route path={this.props.urls.groupSetPath(':id')} render={() => <GroupSetSpaceRouter {...this.props} />} />;
  }

  private getScheduleRoute(server: SessionManagementService) {
    return server.isUserAuthorized(AuthLevel.SiteAdmin) &&
      <Route path={this.props.urls.schedulePath(':id')} render={() => <ScheduleSpaceRouter {...this.props} />} />;
  }

  private getGroupRoute(server: SessionManagementService) {
    return this.canAccessAdminRoute('Group') &&
      <Route path={this.props.urls.groupPath(':id')} render={() => <GroupSpaceRouter {...this.props} />} />;
  }

  private siteSpaceprovider = (siteSpaceContentType: SiteSpaceContentType): (props: RouteComponentProps) => JSX.Element => {
    return (props: RouteComponentProps): JSX.Element => {
      const siteSpaceProviderProps: SiteSpaceProviderProps = {
        siteName: this.props.siteName,
        siteSpaceContentType,
        routeLocationSearch: props.location.search,
        routeLocationPath: props.location.pathname,
        urls: this.props.urls,
      };

      return <SiteSpaceProvider {...siteSpaceProviderProps} key={'1'} />;
    };
  };

  private getSiteStatusVizRoute(server: SessionManagementService) {
    return server.isUserAuthorized(AuthLevel.SiteAdmin) &&
      <Route path={this.props.urls.siteStatusVizPath(':sheetUrlId')}
        render={(routeProps) => <AdminAnalysisVizSpaceBreadcrumbsAndTitleUpdater.ConnectedUpdater
          sheetUrlId={routeProps.match.params.sheetUrlId}
          routeLocationSearch={routeProps.location.search}
          history={routeProps.history}
          urls={this.props.urls}
          isServerAnalysis={false}
          renderSpaceProvider={AdminAnalysisVizSpaceBreadcrumbsAndTitleUpdater.renderProvider}
        />}
      />;
  }

  private getVizRoute() {
    return <Route path={this.props.urls.viewPath(':viewPath')}
      render={(routeProps) => {
        return (<VizSpaceUrlUpdater.ConnectedContainer
          vizPathFromUrl={VizUrl.getVizPathFromUrlLocation(routeProps.location)}
          routeLocationSearch={routeProps.location.search}
          urls={this.props.urls}
          renderProvider={VizSpaceUrlUpdater.renderProvider}
          grantAccessDisplay={this.isGrantAccessDisplayEnabled(this.props, routeProps.location.pathname)}
          grantAccessUrl={this.props.previousUrl}
        />);
      }}
    />;
  }

  private isGrantAccessDisplayEnabled(props: SiteSpaceRouterComponentProps, pathName: string) {
    // For grant access view scenarios where the url is redirected from the grant access view url to the
    // actual view url, grant access dialog should be displayed on the UI.  We need to extract the
    // <WorkbookName> and <ViewName> from the pathName to figure out this. For default sites the pathName
    // looks like this "/views/<WorkbookName>/<ViewName>" and for custom sites the pathName looks like
    // this "/site/<SiteName>/views/<WorkbookName>/<ViewName>".
    const length = pathName.split('/').length;
    if (pathName && length > 1) {
      return (props.previousUrl.includes(props.urls.viewGrantAccessPath(pathName.split('/')[length - 2], pathName.split('/')[length - 1])));
    }
  }

  private favoritesSpace = (props: RouteComponentProps): JSX.Element => (
    <ConnectedFavoritesSpace
      siteName={this.props.siteName}
      routeLocationPath={props.location.pathname}
      routeLocationSearch={props.location.search}
    />
  );

  private recycleBinSpace = (props: RouteComponentProps): JSX.Element => (
    <ConnectedRecycleBinSpace
      siteName={this.props.siteName}
      routeLocationPath={props.location.pathname}
      routeLocationSearch={props.location.search}
    />
  );

  private recentsSpace = (props: RouteComponentProps): JSX.Element => (
    <RecentsSpaceProvider
      siteName={this.props.siteName}
      routeLocationPath={props.location.pathname}
      routeLocationSearch={props.location.search}
    />
  );


  private exploreDatasourceProvider = (publishedDatasourceId: string): JSX.Element => (
    <ExploreDatasourceProvider publishedDatasourceId={publishedDatasourceId} urls={this.props.urls} />
  );

  private redirectToFavoritedDatasourceProvider = (idForFavoriting: string, datasourceId?: string): JSX.Element => (
    <RedirectToFavoritedDatasourceProvider idForFavoriting={idForFavoriting} datasourceId={datasourceId} urls={this.props.urls} />
  );

  private redirectToFavoritedWorkbookProvider = (workbookId?: string): JSX.Element => (
    <RedirectToFavoritedWorkbookProvider workbookId={workbookId} urls={this.props.urls} />
  );

  private redirectToFavoritedProjectProvider = (projectId?: string): JSX.Element => (
    <RedirectToFavoritedProjectProvider projectId={projectId} urls={this.props.urls} />
  );

  private redirectToViewProvider = (viewId: string, redirectType: RedirectViewType, username?: string, customViewUrlId?: string, customViewLuid?: string): JSX.Element => (
    <ConnectedRedirectToViewSpaceProvider viewId={viewId} customViewUrlId={customViewUrlId} urls={this.props.urls} redirectType={redirectType} customViewLuid={customViewLuid} />
  );

  private redirectToFlowProvider = (flowId: string) => (
    <FlowSpaceFavoriteRedirect flowId={flowId} urls={this.props.urls} />
  );

  private redirectToPublishedConnectionProvider = (publishedConnectionId: string) => (
    <PublishedConnectionSpaceFavoriteRedirect publishedConnectionId={publishedConnectionId} urls={this.props.urls} />
  );

  private redirectToPublishedConnectionTableProvider = (publishedConnectionTableId: string) => (
    <PublishedConnectionTableSpaceFavoriteRedirect publishedConnectionTableId={publishedConnectionTableId} urls={this.props.urls} />
  );

  private error = (): JSX.Element => {
    return <RouterError errorType={'default'} urls={this.props.urls} />;
  };

  private getSiteStatusRoute(server: SessionManagementService): React.ReactNode {
    return server.isUserAuthorized(AuthLevel.SiteAdmin) &&
      <Route
        path={this.props.urls.statusPath()}
        render={() =>
          <SiteStatusSpaceProvider
            siteName={this.props.siteName}
            urls={this.props.urls}
          />
        }
      />;
  }

  private getLabelsRoute(server: SessionManagementService): React.ReactNode {
    return server.isUserAuthorized(AuthLevel.SiteAdmin) &&
      <Route exact path={this.props.urls.labelsPath()}
        render={(routeProps) =>
          <>
            <CatalogProviders>
              <AllLabelsSpaceProvider
                siteName={this.props.siteName}
                routeLocationSearch={routeProps.location.search}
                routeLocationPath={routeProps.location.pathname}
                urls={this.props.urls}
              />
            </CatalogProviders>
          </>

        }
      />;
  }

  private getUsersRoute(server: SessionManagementService): React.ReactNode {
    if (FeatureFlags.identityMigrationEnabled && server.isItASingleSiteServer() &&
      this.server.isUserAuthorized(AuthLevel.ServerAdmin)) {
      // For SingleSite OnPrem we split the Users space into two subspaces, Site Users and Identities.
      return <Route path={this.props.urls.usersPath()} render={(routeProps) =>
        <AllSiteUsersAndIdentitiesForSingleSiteSpaceRouter
          routeLocationPath={routeProps.location.pathname}
          routeLocationSearch={routeProps.location.search}
          siteName={this.props.siteName}
          urls={this.props.urls}
        />}
      />;
    }
    // When fetching permissions (admin profiles on, non-admin), show loading - don't grant or deny yet
    const loadingRoute = this.adminPermissionsLoadingRoute(this.props.urls.usersPath());
    if (loadingRoute) {
      return loadingRoute;
    }
    return this.canAccessAdminRoute('User') &&
      <Route path={this.props.urls.usersPath()} render={(routeProps) =>
        <AllSiteUsersSpaceProvider
          routeLocationPath={routeProps.location.pathname}
          routeLocationSearch={routeProps.location.search}
          siteName={this.props.siteName}
          urls={this.props.urls}
        />}
      />;
  }

  private getSharedWithMeRoute(): React.ReactNode {
    return <Route path={this.props.urls.sharedWithMePath()}
      render={(routeProps) => <SharedWithMeSpaceProvider
        routeLocationSearch={routeProps.location.search}
        routeLocationPath={routeProps.location.pathname}
      />}
    />;
  }

  private getPersonalSpaceRoute(server: SessionManagementService): React.ReactNode {
    return server.shouldShowPersonalSpace() &&
      <Route path={this.props.urls.personalSpacePath()}
        render={(routeProps) => <PersonalSpaceProvider
          routeLocationSearch={routeProps.location.search}
          routeLocationPath={routeProps.location.pathname}
          urls={this.props.urls}
        />}
      />;
  }

  private getStartersRouteByConnectionType() {
    return <Route
      exact path={this.props.urls.startersByConnectionTypePath(':starters')}
      render={(routeProps) => <StartersSpaceProvider
        routeLocationSearch={routeProps.location.search}
        routeLocationPath={routeProps.location.pathname}
        urls={this.props.urls}
        connectionType={routeProps.match.params.starters}
      />}
    />;
  }

  private getSiteTasksRoute(server: SessionManagementService): React.ReactNode {
    return server.isUserAuthorized(AuthLevel.SiteAdmin) &&
      <Route path={this.props.urls.tasksPath()} render={() => <SiteTasksSpaceRouter {...this.props} />} />;
  }
}

const mapStateToProps: MapStateToProps<SiteSpaceRouterStateProps> = (state: AppState) => {
  const isFetching = state.siteAdminResources.isFetching;
  const isAdminProfilesEnabled = getSessionManagementServiceInstance().isAdminProfilesEnabled();
  // Treat as loading if actively fetching, OR if admin-profiles is on and we have never fetched
  // yet (undefined resources, not fetching). This prevents a false-negative access denial on the
  // first render of a cold deep-link before componentDidMount dispatches the fetch.
  const neverFetched = isAdminProfilesEnabled && !isFetching && state.siteAdminResources.allowedResources === undefined;
  return {
    previousUrl: selectPreviousUrl(state),
    urls: siteUrlsSelector(state),
    routerReactKey: state.routerSpaceReactKey,
    canPublish: SessionSiteSelectors.isSessionCanPublish(state),
    allowedSiteAdminResources: state.siteAdminResources.allowedResources,
    isFetchingSiteAdminResources: isFetching || neverFetched,
    canUseAISearch: !!SessionUserSelectors.canUseAISearch(state)
  };
};

const mapDispatchToProps: MapDispatchToProps<SiteSpaceRouterDispatchProps> = (dispatch) => bindActionCreators({
  fetchSiteAdminResources
}, dispatch);

export const ConnectedSiteSpaceRouter: React.ComponentClass<SiteSpaceRouterOwnProps>
  = connect(mapStateToProps, mapDispatchToProps)(SiteSpaceRouterComponent);

export interface SiteSpaceRouterProps extends SiteSpaceRouterOwnProps {
  readonly isBusy: boolean;
}

export class SiteSpaceRouter extends React.Component<SiteSpaceRouterProps> {
  public render() {
    if (this.props.isBusy) {
      // Render a blank page while we in are in middle of switching sites (isBusy===true)
      // In this point the url will contain the new site but we aren't ready to display
      // any data yet.
      return null;
    }

    return (
      <ConnectedReduxRouterErrorWrapper>
        <CatalogProviders>
        <ConnectedSiteSpaceRouter
          siteName={this.props.siteName}
          routeLocationPath={this.props.routeLocationPath}
          routeLocationSearch={this.props.routeLocationSearch}
        />
        </CatalogProviders>
      </ConnectedReduxRouterErrorWrapper>
    );
  }
}
